import React, { useEffect, useState, useRef } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area } from 'recharts';
import { XMarkIcon, ChartBarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import html2canvas from 'html2canvas';

interface ChartDataPoint {
    period: string;
    [key: string]: string | number; // Allow dynamic keys for multiple metrics
}

interface ChartDataset {
    label: string;
    units?: string;
    color: string;
}

interface FinancialChartModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: ChartDataPoint[];
    datasets: ChartDataset[];
    onRemoveDataset?: (label: string) => void;
}

export const FinancialChartModal: React.FC<FinancialChartModalProps> = ({
    isOpen,
    onClose,
    data,
    datasets,
    onRemoveDataset
}) => {
    const [chartType, setChartType] = useState<'bar' | 'line' | 'area'>('bar');
    const chartRef = useRef<HTMLDivElement>(null);
    
    const handleEscPressed = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') {
                onClose();
            }
        }

    useEffect(()=>{
        window.addEventListener('keypress', handleEscPressed)

        return () => {
            window.removeEventListener('keypress', handleEscPressed)
        }
    },[])

    const downloadChart = async (format: 'png' | 'jpeg' = 'png') => {
        if (!chartRef.current) return;
        
        try {
            const canvas = await html2canvas(chartRef.current, {
                backgroundColor: '#ffffff',
                scale: 2, // Higher resolution
                logging: false,
                useCORS: true
            });
            
            const link = document.createElement('a');
            link.download = `financial-chart-${new Date().toISOString().split('T')[0]}.${format}`;
            link.href = canvas.toDataURL(`image/${format}`, 0.9);
            link.click();
        } catch (error) {
            console.error('Error downloading chart:', error);
        }
    };

    if (!isOpen) return null;

    // Define colors for different datasets
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];

    const formatValue = (value: number, units?: string) => {
        // Helper function to truncate to 2 decimal places without rounding
        const truncateToTwoDecimals = (num: number): number => {
            const str = num.toString();
            const decimalIndex = str.indexOf('.');
            if (decimalIndex === -1) return num;
            const truncatedStr = str.substring(0, decimalIndex + 3); // Keep 2 decimal places
            return parseFloat(truncatedStr);
        };

        if (units && units.toLowerCase().includes('usd')) {
            if (Math.abs(value) >= 1000000000) {
                return `$${truncateToTwoDecimals(value / 1000000000)}B`;
            } else if (Math.abs(value) >= 1000000) {
                return `$${truncateToTwoDecimals(value / 1000000)}M`;
            } else if (Math.abs(value) >= 1000) {
                return `$${truncateToTwoDecimals(value / 1000)}K`;
            } else {
                return `$${truncateToTwoDecimals(value) || '0'}`;
            }
        }
        
        // For non-USD values (like ratios, percentages, etc.)
        if (Math.abs(value) >= 1000000000) {
            return `${truncateToTwoDecimals(value / 1000000000)}B`;
        } else if (Math.abs(value) >= 1000000) {
            return `${truncateToTwoDecimals(value / 1000000)}M`;
        } else if (Math.abs(value) >= 1000) {
            return `${truncateToTwoDecimals(value / 1000)}K`;
        } else {
            return truncateToTwoDecimals(value)?.toString() || '0';
        }
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
                    <p className="font-semibold mb-2">{`${label}`}</p>
                    {payload.map((entry: any, index: number) => {
                        const dataset = numericDatasets.find(d => d.label === entry.dataKey);
                        return (
                            <p key={index} className="text-sm" style={{ color: entry.color }}>
                                {`${entry.dataKey}: ${formatValue(entry.value, dataset?.units)}`}
                            </p>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    // Separate numeric and non-numeric datasets, excluding 'Fiscal Year' from plotting
    const numericDatasets = datasets.filter(dataset => {
        // Exclude 'Fiscal Year' column from plotting
        if (dataset.label === 'Fiscal Year') {
            return false;
        }
        // Check if the dataset has at least one numeric value
        return data.some(point => {
            const value = point[dataset.label];
            return typeof value === 'number' && !isNaN(value);
        });
    });

    // Filter out data points with zero or null values for FY periods, and sort chronologically (latest on right)
    const validData = data
        .filter(point => {
            // Keep point if at least one numeric dataset has a non-zero value AND it's not an FY period
            const hasNonZeroValue = numericDatasets.some(dataset => {
                const value = point[dataset.label];
                return value !== null && value !== 0;
            });
            
            // Only filter out Q4 periods if there's a corresponding FY period for the same year
            // AND the Q4 and FY values are identical (indicating they're the same data point)
            let isDuplicateQ4 = false;
            if (point.period.match(/Q4\s*'(\d+)/)) {
                const q4Year = point.period.match(/Q4\s*'(\d+)/)?.[1];
                const fyPoint = data.find(otherPoint => 
                    otherPoint.period === `FY '${q4Year}`
                );
                
                if (fyPoint) {
                    // Check if all numeric dataset values are identical between Q4 and FY
                    isDuplicateQ4 = numericDatasets.every(dataset => {
                        const q4Value = point[dataset.label];
                        const fyValue = fyPoint[dataset.label];
                        return q4Value === fyValue;
                    });
                }
            }
            
            return hasNonZeroValue && !isDuplicateQ4;
        })
        .sort((a, b) => {
            // Extract year and quarter/period from period string (e.g., "Q1 '24", "Q2 '24")
            const parseDate = (period: string) => {
                const match = period.match(/([Q]?\d+)\s*'(\d+)/);
                if (match) {
                    const [, periodPart, yearPart] = match;
                    const year = parseInt('20' + yearPart);
                    
                    // Handle quarters (Q1, Q2, etc.) vs annual periods
                    if (periodPart.startsWith('Q')) {
                        const quarter = parseInt(periodPart.substring(1));
                        return year * 10 + quarter; // e.g., 2024Q1 = 20241
                    } else {
                        const period = parseInt(periodPart);
                        return year * 100 + period; // e.g., 2024 annual = 202400
                    }
                }
                return 0;
            };
            
            return parseDate(a.period) - parseDate(b.period);
        });

    // For the table, include FY items and sort with latest dates at the top
    const tableData = data
        .filter(point => {
            // Keep point if at least one dataset (numeric or text) has a meaningful value
            return datasets.some(dataset => {
                const value = point[dataset.label];
                // For numeric values, check if non-null and not zero
                if (typeof value === 'number') {
                    return !isNaN(value) && value !== null;
                }
                // For text values, check if non-empty
                return value !== null && value !== undefined && value !== '';
            });
        })
        .sort((a, b) => {
            // Extract year and quarter/period from period string (e.g., "Q1 '24", "Q2 '24", "FY '24")
            const parseDate = (period: string) => {
                const match = period.match(/(FY|[Q]?\d+)\s*'(\d+)/);
                if (match) {
                    const [, periodPart, yearPart] = match;
                    const year = parseInt('20' + yearPart);
                    
                    // Handle FY (full year), quarters (Q1, Q2, etc.), vs other periods
                    if (periodPart === 'FY') {
                        return year * 100 + 99; // e.g., FY 2024 = 202499 (sorts after Q4)
                    } else if (periodPart.startsWith('Q')) {
                        const quarter = parseInt(periodPart.substring(1));
                        return year * 100 + quarter; // e.g., 2024Q1 = 202401
                    } else {
                        const period = parseInt(periodPart);
                        return year * 100 + period;
                    }
                }
                return 0;
            };
            
            return parseDate(b.period) - parseDate(a.period); // Reverse order (latest first)
        });

    const renderChart = () => {
        const chartProps = {
            data: validData,
            margin: { top: 20, right: 30, left: 20, bottom: 5 }
        };

        const commonElements = (
            <>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                    dataKey="period" 
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                />
                <YAxis 
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => formatValue(value)}
                />
                <Tooltip content={<CustomTooltip />} />
            </>
        );

        switch (chartType) {
            case 'line':
                return (
                    <LineChart {...chartProps}>
                        {commonElements}
                        {numericDatasets.map((dataset, index) => (
                            <Line
                                key={dataset.label}
                                type="monotone"
                                dataKey={dataset.label}
                                stroke={colors[index % colors.length]}
                                strokeWidth={2}
                                dot={{ fill: colors[index % colors.length], strokeWidth: 2, r: 4 }}
                                activeDot={{ r: 6, stroke: colors[index % colors.length], strokeWidth: 2 }}
                            />
                        ))}
                    </LineChart>
                );
            case 'area':
                return (
                    <AreaChart {...chartProps}>
                        {commonElements}
                        {numericDatasets.map((dataset, index) => (
                            <Area
                                key={dataset.label}
                                type="monotone"
                                dataKey={dataset.label}
                                stackId="1"
                                stroke={colors[index % colors.length]}
                                fill={colors[index % colors.length]}
                                fillOpacity={0.6}
                            />
                        ))}
                    </AreaChart>
                );
            case 'bar':
            default:
                return (
                    <BarChart {...chartProps}>
                        {commonElements}
                        {numericDatasets.map((dataset, index) => (
                            <Bar
                                key={dataset.label}
                                dataKey={dataset.label}
                                fill={colors[index % colors.length]}
                                radius={[2, 2, 0, 0]}
                            />
                        ))}
                    </BarChart>
                );
        }
    };

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={onClose}
        >
            <div 
                className="bg-white rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <ChartBarIcon className="h-6 w-6 text-blue-500 mr-2" />
                        <h2 className="text-xl font-semibold text-gray-900">
                            Financial Metrics Comparison
                        </h2>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Download Button */}
                        <button
                            onClick={() => downloadChart('png')}
                            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-50 transition-colors"
                            title="Download chart as PNG"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Download
                        </button>
                        {/* Chart Type Selector */}
                        <select
                            value={chartType}
                            onChange={(e) => setChartType(e.target.value as 'bar' | 'line' | 'area')}
                            className="text-sm border w-[7rem] border-gray-300 rounded-md px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="bar">Bar Chart</option>
                            <option value="line">Line Chart</option>
                            <option value="area">Area Chart</option>
                        </select>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                {/* Dataset Legend with Remove Buttons */}
                <div ref={chartRef} className="bg-white">
                    {numericDatasets.length > 0 && (
                        <div className="mb-4 flex flex-wrap gap-2">
                            {numericDatasets.map((dataset, index) => (
                                <div key={dataset.label} className="flex items-center bg-gray-50 rounded-lg px-3 py-1" style={{ lineHeight: '1.25rem' }}>
                                    <div 
                                        className="w-3 h-3 rounded-full mr-2 flex-shrink-0" 
                                        style={{ 
                                            backgroundColor: colors[index % colors.length],
                                            marginTop: '0.125rem' // Slight adjustment for perfect alignment in screenshots
                                        }}
                                    ></div>
                                    <span className="text-sm font-medium text-gray-700 mr-2" style={{ lineHeight: '1.25rem' }}>{dataset.label}</span>
                                    {onRemoveDataset && (
                                        <button
                                            onClick={() => onRemoveDataset(dataset.label)}
                                            className="text-gray-400 hover:text-red-500 text-xs"
                                            style={{ lineHeight: '1.25rem' }}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {validData.length > 0 ? (
                        <div className="h-96">
                            <ResponsiveContainer width="100%" height="100%">
                                {renderChart()}
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="h-96 flex items-center justify-center text-gray-500">
                            <div className="text-center">
                                <ChartBarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                                <p>No data available for visualization</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Data table below the chart */}
                {tableData.length > 0 && (
                    <div className="mt-6 max-h-40 overflow-y-auto">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Data Points</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Period
                                        </th>
                                        {datasets.map((dataset) => (
                                            <th key={dataset.label} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {dataset.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {tableData.map((point, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                                                {point.period}
                                            </td>
                                            {datasets.map((dataset) => {
                                                const value = point[dataset.label];
                                                let formattedValue;
                                                
                                                if (typeof value === 'number' && !isNaN(value)) {
                                                    // Don't format 'Fiscal Year' values with currency
                                                    if (dataset.label === 'Fiscal Year') {
                                                        formattedValue = value.toString();
                                                    } else {
                                                        // Only format numbers that are not NaN
                                                        formattedValue = formatValue(value, dataset.units);
                                                    }
                                                } else if (value !== null && value !== undefined && value !== '') {
                                                    // Display text values as-is
                                                    formattedValue = value
                                                } else {
                                                    // Display dash for empty/null values
                                                    formattedValue = '-';
                                                }
                                                
                                                return (
                                                    <td key={dataset.label} className="px-3 py-2 whitespace-nowrap text-right text-gray-900">
                                                        {formattedValue}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
